import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import {
	CebeDto,
	CebeCreateDto,
	CebeDetailDto,
	CebeOperativoViewModel,
	CebeOperativoCreateDto,
	CebeOperativoUpdateDto,
	CebeIntegracionLibreViewModel,
	UpdateDatosOperativosCebeDto,
} from "../interfaces/cebe.interface";
import { CebeLookup } from "../interfaces/cebe-lookup.interface";
import { CentroProcedure } from "src/app/_core/models/centros/centro-procedure.model";
import { environment } from "src/environments/environment";

@Injectable({
	providedIn: "root",
})
export class CebeService {
	private readonly apiUrl = `${environment.api.root}/Cebe`;
	private readonly operativoUrl = `${environment.api.root}/CebeOperativo`;

	constructor(private readonly http: HttpClient) {}

	/**
	 * Obtiene todos los cebes
	 */
	getCebesList(): Observable<CebeDto[]> {
		return this.http.get<CebeDto[]>(`${this.apiUrl}`);
	}

	/**
	 * Lookup mínimo de cebes para selectores / binding (asociación a contrato).
	 * Devuelve solo cabecera; no expone el detalle completo.
	 */
	getLookup(): Observable<CebeLookup[]> {
		return this.http.get<CebeLookup[]>(`${this.apiUrl}/Lookup`);
	}

	/**
	 * Obtiene un cebe por su ID
	 */
	getCebeById(id: number): Observable<CebeDto> {
		return this.http.get<CebeDto>(`${this.apiUrl}/${id}`);
	}

	/**
	 * Obtiene el detalle completo de un cebe incluyendo elementos PEP
	 */
	getCebeDetailById(id: number): Observable<CebeDetailDto> {
		return this.http.get<CebeDetailDto>(`${this.apiUrl}/${id}/details`);
	}

	/**
	 * Actualiza un cebe existente (solo fechas/estado/observaciones; el resto lo gobierna la ETL).
	 */
	updateCebe(id: number, cebe: CebeCreateDto): Observable<CebeDto> {
		return this.http.put<CebeDto>(`${this.apiUrl}/${id}`, cebe);
	}

	/**
	 * Obtiene cebes filtrados por país
	 */
	getCebesByPais(paisId: number): Observable<CebeDto[]> {
		const params = new HttpParams().set("paisId", paisId.toString());
		return this.http.get<CebeDto[]>(`${this.apiUrl}`, { params });
	}

	/**
	 * Busca cebes por código o descripción
	 */
	searchCebes(searchTerm: string): Observable<CebeDto[]> {
		const params = new HttpParams().set("search", searchTerm);
		return this.http.get<CebeDto[]>(`${this.apiUrl}/search`, { params });
	}

	/**
	 * Obtiene los CEBEs asociados a un contrato
	 */
	getCebesByContratoId(contratoId: number): Observable<CebeDto[]> {
		return this.http.get<CebeDto[]>(
			`${this.apiUrl}/GetCebesByContratoId/${contratoId}`,
		);
	}

	/**
	 * Obtiene los CEBEs que no están asignados a ningún contrato
	 */
	getCebesSinAsignarContrato(): Observable<CebeDto[]> {
		return this.http.get<CebeDto[]>(
			`${this.apiUrl}/GetCebesSinAsignarContrato`,
		);
	}

	/**
	 * Asigna un CEBE a un contrato
	 */
	asignarContratoCebe(contratoId: number, cebeId: number): Observable<void> {
		return this.http.post<void>(`${this.apiUrl}/AsignarContratoCebe`, {
			contratoId,
			cebeId,
		});
	}

	/**
	 * Desasocia un CEBE de su contrato
	 */
	removeFromContrato(cebeId: number): Observable<void> {
		return this.http.delete<void>(
			`${this.apiUrl}/RemoveFromContrato/${cebeId}`,
		);
	}

	// ─────────────────────────────────────────────────────────────────────────
	// US-EVO00002: CeBe Operativo
	// ─────────────────────────────────────────────────────────────────────────

	/**
	 * Obtiene los CeBes operativos vinculados a un contrato padre
	 */
	getCebesOperativosByContrato(
		contratoId: number,
	): Observable<CebeOperativoViewModel[]> {
		// Patrón Lookup (capacidad List, misma proyección): contexto del contrato
		// accesible a perfiles acotados (p.ej. responsable de contrato sin Cebe.View).
		return this.http.get<CebeOperativoViewModel[]>(
			`${this.operativoUrl}/contrato/${contratoId}/lookup`,
		);
	}

	/**
	 * Obtiene un CeBe operativo por ID
	 */
	getCebeOperativo(id: number): Observable<CebeOperativoViewModel> {
		return this.http.get<CebeOperativoViewModel>(
			`${this.operativoUrl}/${id}`,
		);
	}

	/**
	 * Crea un CeBe operativo (promoción desde integración o manual)
	 */
	createCebeOperativo(
		dto: CebeOperativoCreateDto,
	): Observable<CebeOperativoViewModel> {
		return this.http.post<CebeOperativoViewModel>(
			`${this.operativoUrl}`,
			dto,
		);
	}

	/**
	 * Actualiza un CeBe operativo
	 */
	updateCebeOperativo(
		id: number,
		dto: CebeOperativoUpdateDto,
	): Observable<CebeOperativoViewModel> {
		return this.http.put<CebeOperativoViewModel>(
			`${this.operativoUrl}/${id}`,
			dto,
		);
	}

	/**
	 * Elimina un CeBe operativo
	 */
	deleteCebeOperativo(id: number): Observable<void> {
		return this.http.delete<void>(`${this.operativoUrl}/${id}`);
	}

	// ─────────────────────────────────────────────────────────────────────────
	// US-EVO00002: CeBe Integración
	// ─────────────────────────────────────────────────────────────────────────

	/**
	 * Obtiene CeBes de integración libres (sin promover)
	 */
	getCebesIntegracionLibres(): Observable<CebeIntegracionLibreViewModel[]> {
		return this.http.get<CebeIntegracionLibreViewModel[]>(
			`${this.operativoUrl}/integracion/libres`,
		);
	}

	/**
	 * Obtiene los centros productivos asociados a un CeBe operativo
	 */
	getCentrosByCebeId(cebeId: number): Observable<CentroProcedure[]> {
		return this.http.get<CentroProcedure[]>(
			`${this.operativoUrl}/${cebeId}/centros`,
		);
	}

	/**
	 * Cambia la fase de un CeBe operativo (Construccion → OperacionMantenimiento)
	 */
	cambiarFase(cebeId: number, nuevaFase: string): Observable<void> {
		return this.http.patch<void>(`${this.operativoUrl}/${cebeId}/fase`, {
			nuevaFase,
		});
	}

	/**
	 * Actualiza únicamente los datos operativos: Responsable, Zona, Actividad, TipoProyecto
	 */
	updateDatosOperativos(
		cebeId: number,
		dto: UpdateDatosOperativosCebeDto,
	): Observable<void> {
		return this.http.patch<void>(
			`${this.operativoUrl}/${cebeId}/datos-operativos`,
			dto,
		);
	}
}
